import { useCallback, useEffect, useState } from 'react';
import {
  type InboundAlias,
  createInboundAlias,
  deleteInboundAlias,
  getCarriers,
  getInboundAliases,
} from '../lib/internal.js';
import { useAsyncData } from '../lib/useAsyncData.js';

const inp = 'w-full rounded-md border border-neutral-300 px-2 py-1.5 text-sm';

/**
 * Admin: manage a customer account's inbound statement-email addresses — set the
 * per-carrier "to" address on their behalf. Pairs with the customer-facing card on the
 * app's Files page. Toggle the `emailIngest` entitlement (Entitlements panel) to expose
 * it in the app.
 */
export function InboundAliasesPanel({ accountId }: { accountId: string }) {
  const { data, error, reload } = useAsyncData(
    useCallback(() => getInboundAliases(accountId), [accountId]),
  );
  const [carriers, setCarriers] = useState<{ id: string; name: string }[]>([]);
  const [carrierId, setCarrierId] = useState('');
  const [sender, setSender] = useState('');
  const [regex, setRegex] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  useEffect(() => {
    getCarriers()
      .then(setCarriers)
      .catch(() => {});
  }, []);

  const add = async () => {
    if (!carrierId) return;
    setBusy(true);
    setMsg(null);
    try {
      await createInboundAlias(accountId, {
        carrierId,
        allowedSenderDomain: sender.trim() || undefined,
        filenamePeriodRegex: regex.trim() || undefined,
      });
      setCarrierId('');
      setSender('');
      setRegex('');
      await reload();
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Failed');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: string) => {
    await deleteInboundAlias(accountId, id);
    await reload();
  };
  const copy = (addr: string) => {
    navigator.clipboard?.writeText(addr).then(() => {
      setCopied(addr);
      setTimeout(() => setCopied(null), 1500);
    });
  };

  if (error) return <span className="text-xs text-rose-600">Couldn’t load inbound addresses</span>;

  return (
    <div className="rounded-lg border border-neutral-200/70 bg-white p-3">
      <div className="mb-2 text-xs font-medium uppercase tracking-wide text-neutral-500">
        Email ingestion — inbound addresses
      </div>
      {!data ? (
        <span className="text-xs text-neutral-400">Loading…</span>
      ) : data.aliases.length === 0 ? (
        <p className="text-sm text-neutral-500">No inbound addresses yet.</p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-neutral-500">
              <th className="py-1">Carrier</th>
              <th className="py-1">Address</th>
              <th className="py-1">Sender</th>
              <th className="py-1">Last</th>
              <th className="py-1" />
            </tr>
          </thead>
          <tbody>
            {data.aliases.map((a: InboundAlias) => (
              <tr key={a.id} className="border-t border-neutral-100 align-top">
                <td className="py-1.5">{a.carrierName ?? a.carrierId.slice(0, 8)}</td>
                <td className="py-1.5">
                  <button
                    type="button"
                    onClick={() => copy(a.address)}
                    className="font-mono text-xs text-emerald-700 hover:underline"
                    title="Copy"
                  >
                    {a.address} {copied === a.address ? '✓' : ''}
                  </button>
                </td>
                <td className="py-1.5 text-xs text-neutral-500">
                  {a.allowedSenderDomain ?? 'any'}
                </td>
                <td className="py-1.5 text-xs text-neutral-500">{a.lastStatus ?? '—'}</td>
                <td className="py-1.5 text-right">
                  <button
                    type="button"
                    onClick={() => remove(a.id)}
                    className="text-xs font-medium text-rose-600 hover:underline"
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="mt-3 grid items-end gap-2 sm:grid-cols-[1.2fr_1fr_1.4fr_auto]">
        <label className="text-xs">
          <span className="mb-1 block text-neutral-500">Carrier</span>
          <select className={inp} value={carrierId} onChange={(e) => setCarrierId(e.target.value)}>
            <option value="">Select…</option>
            {carriers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        <label className="text-xs">
          <span className="mb-1 block text-neutral-500">Only from (optional)</span>
          <input
            className={inp}
            value={sender}
            placeholder="carrier.com"
            onChange={(e) => setSender(e.target.value)}
          />
        </label>
        <label className="text-xs">
          <span className="mb-1 block text-neutral-500">Filename period rule (optional)</span>
          <input
            className={inp}
            value={regex}
            placeholder="(?<year>\d{4})-(?<month>\d{2})"
            onChange={(e) => setRegex(e.target.value)}
          />
        </label>
        <button
          type="button"
          disabled={busy || !carrierId}
          onClick={add}
          className="rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
        >
          Add address
        </button>
      </div>
      {msg && <p className="mt-2 text-xs text-rose-600">{msg}</p>}
    </div>
  );
}
